/**
 * HMAC-SHA-256 (RFC 2104 over FIPS 180-4), synchronous and with no platform crypto — plan-029 KEYED (ED-11; design
 * §40.9.2, §40.20.1; ADR 0082, proposed).
 *
 * **Why it is written out here.** The keyed-code primitives moved from the api (`node:crypto`'s `createHmac`) into
 * `@wetalk/domain`, which the browser consoles import as well as Node, so this package may use neither `node:crypto`
 * (not in a browser) nor `crypto.subtle` (asynchronous) — the rule `../compliance/e164-hash.ts` states for the phone
 * number hash. That file's SHA-256 is module-private and takes a text, while an HMAC hashes raw bytes (the key XOR the
 * pads), so this module states SHA-256 over bytes once more, for keyed digests only. Its suite pins both functions to
 * `node:crypto` over the standard's vectors and random inputs, so the two can never drift from the platform's.
 *
 * Branch-free over the data: the rounds' work does not depend on the bytes hashed, so the time taken depends on the
 * input's length only (and a code's length is not secret).
 */
/** The first 32 bits of the fractional parts of the cube roots of the first 64 primes (FIPS 180-4 §4.2.2). */
const ROUND_CONSTANT = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
/** The first 32 bits of the fractional parts of the square roots of the first 8 primes (FIPS 180-4 §5.3.3). */
const INITIAL_HASH = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];
/** SHA-256's block, and so HMAC's key block, in bytes. */
const BLOCK_BYTE = 64;
function rotateRight(value, by) {
    return (value >>> by) | (value << (32 - by));
}
/** SHA-256 of `message`. 32 bytes. */
export function sha256Bytes(message) {
    // Padding (§5.1.1): a single 1 bit, zeros, then the message length in bits as a 64-bit big-endian integer.
    const bitLength = message.byteLength * 8;
    const paddedLength = Math.ceil((message.byteLength + 9) / BLOCK_BYTE) * BLOCK_BYTE;
    const padded = new Uint8Array(paddedLength);
    padded.set(message);
    padded[message.byteLength] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000), false);
    view.setUint32(paddedLength - 4, bitLength >>> 0, false);
    const state = Uint32Array.from(INITIAL_HASH);
    const schedule = new Uint32Array(64);
    for (let block = 0; block < paddedLength; block += BLOCK_BYTE) {
        for (let t = 0; t < 16; t += 1)
            schedule[t] = view.getUint32(block + t * 4, false);
        for (let t = 16; t < 64; t += 1) {
            const w15 = schedule[t - 15];
            const w2 = schedule[t - 2];
            const sigma0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3);
            const sigma1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10);
            schedule[t] = (schedule[t - 16] + sigma0 + schedule[t - 7] + sigma1) >>> 0;
        }
        let a = state[0];
        let b = state[1];
        let c = state[2];
        let d = state[3];
        let e = state[4];
        let f = state[5];
        let g = state[6];
        let h = state[7];
        for (let t = 0; t < 64; t += 1) {
            const bigSigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temp1 = (h + bigSigma1 + choose + ROUND_CONSTANT[t] + schedule[t]) >>> 0;
            const bigSigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (bigSigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        state[0] = (state[0] + a) >>> 0;
        state[1] = (state[1] + b) >>> 0;
        state[2] = (state[2] + c) >>> 0;
        state[3] = (state[3] + d) >>> 0;
        state[4] = (state[4] + e) >>> 0;
        state[5] = (state[5] + f) >>> 0;
        state[6] = (state[6] + g) >>> 0;
        state[7] = (state[7] + h) >>> 0;
    }
    const digest = new Uint8Array(32);
    const out = new DataView(digest.buffer);
    for (let word = 0; word < 8; word += 1)
        out.setUint32(word * 4, state[word], false);
    return digest;
}
/** HMAC-SHA-256 of `message` under `key` (RFC 2104 §2): a key longer than a block is hashed first. 32 bytes. */
export function hmacSha256(key, message) {
    const block = new Uint8Array(BLOCK_BYTE);
    block.set(key.byteLength > BLOCK_BYTE ? sha256Bytes(key) : key);
    const inner = new Uint8Array(BLOCK_BYTE + message.byteLength);
    const outer = new Uint8Array(BLOCK_BYTE + 32);
    for (let index = 0; index < BLOCK_BYTE; index += 1) {
        const byte = block[index];
        inner[index] = byte ^ 0x36;
        outer[index] = byte ^ 0x5c;
    }
    inner.set(message, BLOCK_BYTE);
    outer.set(sha256Bytes(inner), BLOCK_BYTE);
    return sha256Bytes(outer);
}
//# sourceMappingURL=hmac-sha256.js.map